<template>
  <BittsModal
    :title="title"
    :width="480"
    :use-mask-to-close="false"
    primary-button-text="Remove partners"
    primary-button-type="danger"
    secondary-button-text="Cancel"
    :disabled="!isInputValid"
    :action-loading="isRemoving"
    @action="emit('remove')"
    @closed="emit('closed')"
  >
    <div class="partner-tab-remove-partners-modal">
      <p class="partner-tab-remove-partners-modal__warning">
        {{ warning }}
      </p>
      <ul
        class="partner-tab-remove-partners-modal__names"
        data-testid="remove-partners-names"
      >
        <li v-for="{ uuid, partner } in partners" :key="uuid">
          {{ partner.name }}
        </li>
      </ul>
      <div class="partner-tab-remove-partners-modal__confirmation">
        <h4 class="partner-tab-remove-partners-modal__heading">
          Type DELETE to confirm
        </h4>
        <BittsInput
          v-model="confirmationInput"
          placeholder="DELETE"
          name="confirmation-input"
          :status="inputStatus"
          danger-text="Please enter the text exactly as displayed to confirm"
        />
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsInput, BittsModal } from '@crossbeam/bitts';

import { pluralize } from 'humanize-plus';
import { computed, ref } from 'vue';

import type { SharingPartnerRow } from '../types';

const { partners, isRemoving = false } = defineProps<{
  partners: Pick<SharingPartnerRow, 'uuid' | 'partner'>[];
  isRemoving?: boolean;
}>();

const emit = defineEmits<{
  remove: [];
  closed: [];
}>();

const partnerCountLabel = computed(
  () => `${partners.length} ${pluralize(partners.length, 'partner')}`,
);

const title = computed(() => `Remove ${partnerCountLabel.value}?`);

const warning = computed(
  () =>
    `Are you sure you want to remove ${partnerCountLabel.value}? This will delete these partnerships and all associated sharing settings.`,
);

const confirmationInput = ref('');

const isInputValid = computed(
  () => confirmationInput.value.toLowerCase() === 'delete',
);

const inputStatus = computed(() =>
  confirmationInput.value.length && !isInputValid.value ? 'danger' : 'default',
);
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-remove-partners-modal {
  @apply flex flex-col gap-16;
}
.partner-tab-remove-partners-modal__warning {
  @apply text-base text-neutral-text;
}
.partner-tab-remove-partners-modal__names {
  @apply flex flex-col gap-4 max-h-[200px] overflow-y-auto rounded-8 bg-neutral-background-weak p-12 text-base text-neutral-text-strong;
}
.partner-tab-remove-partners-modal__confirmation {
  @apply flex flex-col gap-8;
}
.partner-tab-remove-partners-modal__heading {
  @apply text-base font-bold text-neutral-text-strong;
}
</style>
